import React from 'react'
import { Box, Paper, Stack, Typography } from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { viz } from './overviewTheme'

const SEVERITY = {
  critical: { color: viz.critical, icon: ErrorOutlineIcon, label: 'Critical' },
  warning: { color: viz.warning, icon: WarningAmberIcon, label: 'Attention' },
}

const plural = (count, singular, pluralWord) =>
  `${count} ${count === 1 ? singular : (pluralWord ?? `${singular}s`)}`

// Ordered worklist for the day — only items with a non-zero count appear,
// most severe first
export const buildAttentionItems = (data) => {
  if (!data) return []
  const { governance = {}, operations = {}, fleet = {}, workforce = {} } = data

  const items = [
    {
      count: governance.expiredMeds,
      severity: 'critical',
      label: (c) => `${plural(c, 'expired medicine')} still in circulation`,
      url: '/medicines-management',
    },
    {
      count: fleet.vehicleServicesOverdue,
      severity: 'critical',
      label: (c) => `${plural(c, 'vehicle service')} overdue`,
      url: '/fleet',
    },
    {
      count: operations.uncoveredNext7,
      severity: 'warning',
      label: (c) => `${plural(c, 'uncovered shift')} in the next 7 days`,
      url: '/global-roster',
    },
    {
      count: governance.safetyAlertsUnaccepted,
      severity: 'warning',
      label: (c) => `${plural(c, 'safety alert')} awaiting acceptance`,
      url: '/safety-alerts',
    },
    {
      count: governance.openIncidents,
      severity: 'warning',
      label: (c) => `${plural(c, 'incident')} open for investigation`,
      url: '/incidents',
    },
    {
      count: operations.pendingShiftApplications,
      severity: 'warning',
      label: (c) => `${plural(c, 'shift application')} to review`,
      url: '/shift-applications',
    },
    {
      count: governance.newFeedback,
      severity: 'warning',
      label: (c) => `${plural(c, 'piece', 'pieces')} of new patient feedback`,
      url: '/feedback',
    },
    {
      count: governance.medsExpiring30,
      severity: 'warning',
      label: (c) => `${plural(c, 'medicine')} expiring within 30 days`,
      url: '/medicines-management',
    },
    {
      count: fleet.openDefects,
      severity: 'warning',
      label: (c) => `${plural(c, 'new defect')} reported`,
      url: '/defects',
    },
    {
      count: workforce.pendingApplications,
      severity: 'warning',
      label: (c) => `${plural(c, 'employee application')} submitted`,
      url: '/employees/applications',
    },
  ]

  return items
    .filter((item) => (item.count ?? 0) > 0)
    .sort((a, b) => (a.severity === b.severity ? 0 : a.severity === 'critical' ? -1 : 1))
}

const AttentionRail = ({ data }) => {
  const items = buildAttentionItems(data)

  return (
    <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
      <Stack>
        <Typography variant="title">Needs your attention</Typography>
        <Typography variant="smallGrey">
          Prioritised across every module
        </Typography>
      </Stack>
      {items.length === 0 ? (
        <Stack alignItems="center" py={5} gap={1}>
          <CheckCircleOutlineIcon sx={{ fontSize: 42, color: viz.good }} />
          <Typography variant="body2" textAlign="center">
            All clear — nothing is waiting on you right now.
          </Typography>
        </Stack>
      ) : (
        <Stack mt={1.5} gap={1}>
          {items.map((item, index) => {
            const config = SEVERITY[item.severity]
            const Icon = config.icon
            return (
              <Stack
                key={item.url + item.severity + index}
                component={motion.div}
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * index, duration: 0.3 }}
              >
                <Stack
                  component={Link}
                  to={item.url}
                  direction="row"
                  alignItems="center"
                  gap={1.25}
                  sx={{
                    p: 1.25,
                    borderRadius: 1,
                    textDecoration: 'none',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderLeft: `3px solid ${config.color}`,
                    transition: 'background-color 0.15s ease',
                    '&:hover': { backgroundColor: 'anMuted.main' },
                  }}
                >
                  <Icon sx={{ fontSize: 18, color: config.color, flexShrink: 0 }} />
                  <Box flexGrow={1}>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.primary', fontSize: '0.85rem' }}
                    >
                      {item.label(item.count)}
                    </Typography>
                    <Typography variant="smallGrey" sx={{ fontSize: '0.7rem' }}>
                      {config.label}
                    </Typography>
                  </Box>
                  <ArrowForwardIosIcon
                    sx={{ fontSize: 12, color: 'text.disabled', flexShrink: 0 }}
                  />
                </Stack>
              </Stack>
            )
          })}
        </Stack>
      )}
    </Paper>
  )
}

export default AttentionRail
